import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { BRAND, ALCOHOL_DISCLAIMER } from "@/lib/constants";
import { Instagram, Mail, Menu, X } from "lucide-react";

const NAV = [
  { label: "Home", href: "/#home" },
  { label: "Services", href: "/#services" },
  { label: "How It Works", href: "/#how" },
  { label: "Menu", href: "/#menu" },
  { label: "Gallery", href: "/#gallery" },
  { label: "Contact", href: "/#contact" },
];

export function PublicLayout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
        }`}
      >
        <div className="df-container flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo"><Logo imgClassName="h-11 w-auto" /></Link>
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-sm font-medium text-ink/70 hover:text-ink transition-colors" data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                {n.label}
              </a>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/quote" className="btn-dark" data-testid="nav-request-quote">Request a Quote</Link>
          </div>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-cream border-t border-line px-5 py-4 space-y-1 animate-fade-in">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block py-3 text-base font-medium border-b border-line/60">{n.label}</a>
            ))}
            <div className="flex gap-3 pt-4">
              <Link to="/quote" className="btn-dark flex-1" onClick={() => setOpen(false)}>Request a Quote</Link>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer id="contact" className="bg-ink text-white mt-24">
        <div className="df-container py-16 grid gap-12 md:grid-cols-3">
          <div>
            <img src="/logo-white.png" alt="Drunk Fruit" className="h-16 w-auto mb-5" />
            <p className="text-white/60 text-sm max-w-xs">{BRAND.location}. Premium mobile cocktail & mocktail bar service for unforgettable events.</p>
          </div>
          <div>
            <p className="eyebrow text-olive mb-4">Get in touch</p>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-white/80 hover:text-white mb-3 transition-colors" data-testid="footer-email">
              <Mail className="h-4 w-4" /> {BRAND.email}
            </a>
            <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors" data-testid="footer-instagram">
              <Instagram className="h-4 w-4" /> {BRAND.instagram}
            </a>
            <Link to="/quote" className="btn-outline mt-6 border-white/40 text-white hover:bg-white hover:text-ink inline-flex" data-testid="footer-request-quote">Request a Quote</Link>
          </div>
          <div>
            <p className="eyebrow text-olive mb-4">Please note</p>
            <p className="text-white/50 text-xs leading-relaxed">{ALCOHOL_DISCLAIMER}</p>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-white/40 text-xs">
          © {new Date().getFullYear()} Drunk Fruit Mobile Bar. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default PublicLayout;
